import { effect, inject, Injectable } from '@angular/core';
import { datadogRum, DefaultPrivacyLevel } from '@datadog/browser-rum';

import { ProfileService } from '@tfs/auth';

import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../../environment.config';

@Injectable({
  providedIn: 'root',
})
export class DatadogRUMService {
  private config = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
  private userService = inject(ProfileService);

  // Profile and subscription load independently, so the user context is (re)written whenever
  // either lands — `setUser` overwrites, and a late subscription just fills in `customerType`.
  // `setDatadogUserContext()` reads `subscription()`, which registers it as a dependency here.
  //
  // Deliberately NOT self-destroyed after the first run. The subscription request is allowed to
  // fail, and `profile.initializer.ts` handles that by calling `setSubscription(null)` — the value
  // the signal already holds, so it notifies nothing. Waiting on a truthy subscription would then
  // strand the user unidentified for the whole session.
  profileEffect = effect(() => {
    const profile = this.userService.userProfile();
    if (profile?.customerName) {
      this.setDatadogUserContext();
    }
  });

  initDatadogRUM() {
    datadogRum.init({
      applicationId: '1de27066-bd7c-4f73-8837-091a0c5a0e35',
      clientToken: 'pub7208e03326fa088af1ffb1dddbe38ab8',
      site: 'datadoghq.eu',
      service: 'toolhive',
      env: this.getEnvironmentName(),
      version: this.config.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: this.getPrivacyLevel(),
      propagateTraceBaggage: false,
      // Already the browser-rum 7.6.0 defaults — stated explicitly so the resource timings,
      // long tasks and click actions the KPI dashboards rely on can't be lost to a future
      // default change. Do not remove as "redundant".
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
    });
  }

  setDatadogUserContext() {
    const profile = this.userService.userProfile();

    // `customerType` (Direct | Partner | Internal) is what lets the KPI dashboards report on
    // external customers only, by filtering out `@usr.customer_type:Internal`.
    datadogRum.setUser({
      id: profile?.customerId || '',
      name: profile?.customerName || '',
      customerType: this.userService.subscription()?.customerType,
    });
  }

  getPrivacyLevel(): DefaultPrivacyLevel {
    switch (this.getEnvironmentName()) {
      case 'prod':
        return 'mask';
      case 'test':
        return 'mask-user-input';
      case 'dev':
        return 'allow';
      default:
        return 'mask';
    }
  }

  getEnvironmentName(): 'dev' | 'test' | 'prod' {
    switch (this.config.toolLibraryBaseApiUrl) {
      case 'https://bff-library.toolhivesolutions.com':
        return 'prod';
      case 'https://bff-library-test.toolhivesolutions.com':
        return 'test';
      case 'https://bff-library-dev.toolhivesolutions.com':
        return 'dev';
      default:
        return 'dev';
    }
  }
}
